import {
  Badge,
  Button,
  Card,
  makeStyles,
  shorthands,
  Spinner,
  Text,
  Toast,
  ToastBody,
  ToastTitle,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  CheckmarkCircle24Regular,
  Cloud24Regular,
  Copy24Regular,
  ErrorCircle24Regular,
  Info24Regular,
  Key24Regular,
  People24Regular,
  Settings24Regular,
  Shield24Regular,
  ShieldKeyhole24Regular
} from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageBar, MessageBarBody } from '../components/Common/ScrollableMessageBar';
import { GroupAutocomplete } from '../components/GroupAutocomplete';
import { EntraIdConfig } from '../config/general.types';
import { triggerAdminConsent } from '../hooks/useAdminConsent';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { apiClient } from '../services/apiClient';
import { publishGeneralConfig } from '../hooks/useGeneralConfig';
import { EntraGroup } from '../services/groupService';
import { sanitizeReturnPath } from '../utils/urlValidation';
import { useCommonSettingsStyles } from './commonSettingsStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px')
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px')
  },
  titleText: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },
  card: {
    ...shorthands.padding('24px')
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#323130'
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    ...shorthands.gap('12px', '24px'),
    alignItems: 'center'
  },
  configLabel: {
    fontWeight: '500',
    color: '#323130'
  },
  configValue: {
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '13px',
    color: '#0066cc',
    backgroundColor: '#f8f9fa',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('4px'),
    wordBreak: 'break-all',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  permissionsList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px')
  },
  permissionItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px', '12px'),
    backgroundColor: '#f3f2f1',
    ...shorthands.borderRadius('4px')
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px')
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px')
  }
});

export const GeneralConfig: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonSettingsStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<EntraIdConfig | null>(null);
  const [error, setError] = useScrollableMessage<string | null>(null);
  const [defaultGroup, setDefaultGroup] = useState<EntraGroup | null>(null);
  const [defaultGroupName, setDefaultGroupName] = useState('');
  const [savingDefaultGroup, setSavingDefaultGroup] = useState(false);

  useEffect(() => {
    // Check if we're returning from admin consent and retrieve URL params
    const urlParams = new URLSearchParams(window.location.search);
    const adminConsent = urlParams.get('admin_consent');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // Helper: Remove query params from URL
    const cleanUpUrl = () => window.history.replaceState({}, document.title, window.location.pathname);

    // Helper: Validate that the consent is for the expected app
    const isConsentClientIdMatch = (pendingConsent: string, stateData: any) => {
      const consentData = JSON.parse(pendingConsent);
      if (consentData.clientId !== stateData.clientId) {
        console.warn('Consent client ID mismatch:', {
          expected: consentData.clientId,
          received: stateData.clientId
        });
        // Clear mismatched consent data
        sessionStorage.removeItem('pendingAdminConsent');
        return false;
      }
      return true;
    };


    //helper to clear token cache depending on the clientId and then a general clear token if the id's don't match
    const handleTokenCache = (pendingConsent: string | null, stateData: any) => {
      if (pendingConsent) {
        const consentData = JSON.parse(pendingConsent);
        // Only process if the client IDs match
        if (consentData.clientId === stateData.clientId) {
          sessionStorage.removeItem('pendingAdminConsent');
          // Clear the backend token cache for the specific app
          clearTokenCache(consentData.appName);
          return;
        }
      }
      // Fallback if session storage is not available
      clearTokenCache();
    };

    //handle admin consent response
    const handleAdminConsentReturn = () => {
      try {
        // Decode the state to see which app was consented and where to return
        const stateData = JSON.parse(decodeURIComponent(state!));
        const pendingConsent = sessionStorage.getItem('pendingAdminConsent');

        // Validate that the consent is for the expected app
        if (pendingConsent) isConsentClientIdMatch(pendingConsent, stateData);

        // Get the current path without query params
        const currentPath = window.location.pathname;
        // Validate returnPath against an allowlist of same-origin relative paths
        // to prevent open-redirect and javascript: injection attacks.
        // sanitizeReturnPath ensures the path:
        // 1. Is a relative path starting with '/' 
        // 2. Is not a protocol-relative URL (e.g., '//evil.com')
        // 3. Does not contain dangerous schemas (javascript:, data:, vbscript:, file:)
        // 4. Is fully decoded and validated against encoding attacks
        const returnPath = sanitizeReturnPath(stateData.returnPath, '/settings');

        // Navigate back to the original path if it's different from where we are now
        // This handles cases where the redirect URI points to homepage but user was on a different page
        if (returnPath !== currentPath) {
          // Security: Path has been validated by sanitizeReturnPath - safe for navigation
          // Using react-router navigate for secure client-side routing
          navigate(returnPath);
          return;
        }

        // Clear the URL parameters
        cleanUpUrl();

        // Clear token cache for the specific app or fallback
        handleTokenCache(pendingConsent, stateData);
      } catch (err) {
        console.error('Failed to parse consent state:', err);
        // Clear URL params even on error
        cleanUpUrl();
        clearTokenCache();
      }
    };

    // if there is consent error logic
    const handleConsentError = () => {
      //handle consent error by calling the cleanUpURl helper and showing a toast message
      cleanUpUrl();

      //display error toast
      dispatchToast(
        <Toast data-testid="general-config-admin-consent-error-toast">
          <ToastTitle data-testid="general-config-admin-consent-failed-toast-title">Admin consent failed</ToastTitle>
          <ToastBody data-testid="general-config-admin-consent-error-toast-body">{errorDescription ?? error}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
      //call loadConfiguration to reload config after error
      loadConfiguration();
    };

    //main logic where we call handleAdminConsentReturn or handleConsentError based on URL params
    if (adminConsent === 'True' && state) {
      //if the admin consent was successful along with the state param then handle the return
      //call the handleAdminConsentReturn function 
      handleAdminConsentReturn();
    } else if (error) {
      // If there was an error during admin consent call the handleConsentError function
      handleConsentError();
    } else {
      // Normal load
      loadConfiguration();
    }

  }, []);

  // Scroll to Backend App Registration section when navigated from Policies permission banner
  useEffect(() => {
    const navState = location.state as any;
    if (navState?.scrollTo === 'backend-app' && !loading) {
      const el = document.getElementById('backend-app-registration');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.state, loading]);

  const clearTokenCache = async (appName?: string) => {
    try {
      await apiClient.post('/general/clear-token-cache');

      dispatchToast(
        <Toast data-testid="general-config-admin-consent-success-toast">
          <ToastTitle data-testid="general-config-admin-consent-granted-successfully-toast-title">Admin consent granted successfully</ToastTitle>
          <ToastBody data-testid="general-config-token-cache-cleared-reloading-permissions-toast-body">
            {appName ? `Permissions granted for ${appName}. ` : ''}Token cache cleared. Reloading permissions...
          </ToastBody>
        </Toast>,
        { intent: "success" }
      );

      // Wait a moment for the cache to clear, then reload
      setTimeout(() => loadConfiguration(), 1000);
    } catch (error) {
      console.error('Failed to clear token cache:', error);
      // Still try to load configuration even if cache clear fails
      loadConfiguration();
    }
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch configuration from API
      const response = await apiClient.get<EntraIdConfig>('/general/config');

      // Handle both wrapped and direct response formats (same fix as version service)
      const configData = response.data || response;
      setConfig(configData);
      publishGeneralConfig(configData);

      // Load default security group settings
      if (configData.defaultSecurityGroup) {
        setDefaultGroup({
          id: configData.defaultSecurityGroup.groupId,
          displayName: configData.defaultSecurityGroup.displayName,
          description: configData.defaultSecurityGroup.description
        } as EntraGroup);
        setDefaultGroupName(configData.defaultSecurityGroup.displayName);
      } else {
        // Try to load from config settings
        try {
          const groupResponse = await apiClient.get<any>('/config/default-group');
          const groupData: any = groupResponse.data || groupResponse;
          if (groupData.groupId) {
            setDefaultGroup({
              id: groupData.groupId,
              displayName: groupData.displayName,
              description: groupData.description
            } as EntraGroup);
            setDefaultGroupName(groupData.displayName);
          }
        } catch (groupErr) {
          console.log('Default group not configured yet');
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.status === 404 ?
      'Configuration endpoint not found. Please ensure the backend server is running.' :
      `Failed to load configuration: ${err.message || 'Unknown error'}`;
      setError(errorMessage);
      console.error('Error loading configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveDefaultGroupSettings = async () => {
    if (!defaultGroup) {
      dispatchToast(
        <Toast data-testid="general-config-save-default-group-warning-toast">
          <ToastTitle data-testid="general-config-please-select-a-group-first-toast-title">Please select a group first</ToastTitle>
        </Toast>,
        { intent: "warning" }
      );
      return;
    }

    try {
      setSavingDefaultGroup(true);

      // Save to backend config endpoint
      await apiClient.put('/config/default-group', {
        groupId: defaultGroup.id,
        displayName: defaultGroup.displayName,
        description: defaultGroup.description
      });

      dispatchToast(
        <Toast data-testid="general-config-save-default-group-success-toast">
          <ToastTitle data-testid="general-config-default-group-settings-saved-successfully-toast-title">Default group settings saved successfully</ToastTitle>
        </Toast>,
        { intent: "success" }
      );
    } catch (error: any) {
      console.error('Failed to save default group settings:', error);
      dispatchToast(
        <Toast data-testid="general-config-save-default-group-error-toast">
          <ToastTitle data-testid="general-config-failed-to-save-default-group-settings-toast-title">Failed to save default group settings</ToastTitle>
          <ToastBody data-testid="general-config-save-default-group-error-toast-body">{error.message || 'An error occurred while saving default group settings'}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setSavingDefaultGroup(false);
    }
  };

  const handleGroupChange = (group: EntraGroup | null, displayName: string) => {
    setDefaultGroup(group);
    setDefaultGroupName(displayName);
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Permission descriptions
  const getPermissionDescription = (permission: string): string => {
    const descriptions: Record<string, string> = {
      'Mail.Send': 'Send email notifications for policy changes and system alerts',
      'Organization.Read.All': 'Required for Microsoft Teams PowerShell to connect',
      'GroupMember.Read.All': 'Look up groups and group membership',
      'User.ReadBasic.All': 'Look up user basic profile information'
    };
    return descriptions[permission] || 'Required for application functionality';
  };

  // Directory role descriptions
  const getDirectoryRoleDescription = (role: string): string => {
    const descriptions: Record<string, string> = {
      'Teams Administrator': 'Required to manage Teams policies, assignments, and configuration',
      'Global Administrator': 'Full administrative access to all Microsoft 365 services',
      'Application Administrator': 'Manage enterprise applications and service principals'
    };
    return descriptions[role] || 'Administrative role for Azure AD operations';
  };

  // Check if all required permissions are granted
  const requiredFrontendPermissions = ['User.Read'];
  const requiredAzureBackendPermissions = [
    'user_impersonation',
    'Mail.Send',
    'profile',
    'Organization.Read.All'
  ];
  const requiredTeamsBackendPermissions = [
    'user_impersonation',
    'Mail.Send',
    'profile',
    'Organization.Read.All'
  ];

  const missingPermissions = (appPermissions: string[], appType: 'frontend' | 'azure-backend' | 'teams-backend' = 'azure-backend') => {
    let required: string[];
    switch (appType) {
      case 'frontend':
        required = requiredFrontendPermissions;
        break;
      case 'teams-backend':
        required = requiredTeamsBackendPermissions;
        break;
      case 'azure-backend':
      default:
        required = requiredAzureBackendPermissions;
        break;
    }
    return required.filter((p) => !appPermissions.includes(p));
  };

  const handleAdminConsent = (clientId: string, tenantId: string, appName: string, specificPermission?: string) => {
    const result = triggerAdminConsent(clientId, tenantId, appName, specificPermission ? [specificPermission] : undefined);

    if (!result.success) {
      dispatchToast(
        <Toast data-testid="general-config-invalid-consent-url-toast">
          <ToastTitle data-testid="general-config-invalid-consent-url-toast-title">Invalid consent URL</ToastTitle>
          <ToastBody data-testid="general-config-invalid-consent-url-toast-body">
            {result.error}
          </ToastBody>
        </Toast>,
        { intent: "error" }
      );
      return;
    }

    const message = specificPermission ?
    `You will be redirected to grant ${specificPermission} permission for ${appName}` :
    `You will be redirected to grant all missing permissions for ${appName}`;

    dispatchToast(
      <Toast data-testid="general-config-admin-consent-toast">
        <ToastTitle data-testid="general-config-admin-consent-toast-title">Opening admin consent page</ToastTitle>
        <ToastBody data-testid="general-config-admin-consent-info-toast-body">{message}</ToastBody>
      </Toast>,
      { intent: "info" }
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="general-config-loading-container-div">
        <Spinner size="large" label="Loading configuration..." data-testid="general-config-loading-configuration-spinner" />
      </div>);

  }

  if (error || !config) {
    return (
      <div className={styles.container} data-testid="general-config-container-div">
        <MessageBar intent="error" data-testid="general-config-load-error-message-bar">
          <MessageBarBody data-testid="general-config-load-error-message-bar-body">{error || 'Configuration not available'}</MessageBarBody>
        </MessageBar>
      </div>);

  }

  return (
    <div className={styles.container} data-testid="general-config-container-2-div">
      <div className={commonStyles.header} data-testid="general-config-header-div">
        <div className={commonStyles.title} data-testid="general-config-title-div">
          <Settings24Regular style={{ fontSize: '32px', color: '#0047AB' }} data-testid="general-config-settings24-regular" />
          <Text className={styles.titleText} data-testid="general-config-general-configuration-text">General Configuration</Text>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular data-testid="general-config-arrow-left-regular" />}
          onClick={() => navigate('/settings')} data-testid="general-config-back-to-settings-button">

          Back to Settings
        </Button>
      </div>

      {/* Azure Tenant Information */}
      <Card className={styles.card} data-testid="general-config-azure-tenant-card">
        <div className={styles.sectionTitle} data-testid="general-config-section-title-div">
          <Cloud24Regular style={{ color: '#0078d4' }} data-testid="general-config-azure-tenant-cloud24-regular" />
          Tenant Information
        </div>
        <div className={styles.configGrid} data-testid="general-config-config-grid-div">
          <Text className={styles.configLabel} data-testid="general-config-azure-tenant-id-text">Tenant ID:</Text>
          <div className={styles.configValue} data-testid="general-config-config-value-div">
            <span data-testid="general-config-azure-tenant-id-value-span">{config.tenants.azure.id}</span>
            <Button
              appearance="subtle"
              icon={<Copy24Regular data-testid="general-config-copy-azure-tenant-id-copy24-regular" />}
              size="small"
              onClick={() => handleCopyToClipboard(config.tenants.azure.id)} data-testid="general-config-copy-azure-tenant-id-button" />

          </div>
          
          <Text className={styles.configLabel} data-testid="general-config-azure-tenant-name-text">Tenant Name:</Text>
          <div className={styles.configValue} data-testid="general-config-config-value-2-div">
            <span data-testid="general-config-azure-tenant-name-value-span">{config.tenants.azure.name}</span>
          </div>
          
          <Text className={styles.configLabel} data-testid="general-config-azure-domain-text">Domain:</Text>
          <div className={styles.configValue} data-testid="general-config-config-value-3-div">
            <span data-testid="general-config-azure-tenant-domain-value-span">{config.tenants.azure.domain}</span>
          </div>
        </div>
      </Card>

      {/* Teams Tenant (if different) */}
      {!config.tenants.teams.isSameAsAzure &&
      <Card className={styles.card} data-testid="general-config-teams-tenant-card">
          <div className={styles.sectionTitle} data-testid="general-config-section-title-2-div">
        <Cloud24Regular style={{ color: '#6264A7' }} data-testid="general-config-teams-tenant-cloud24-regular" />
            Teams Tenant Information
            <Badge appearance="outline" color="informative" data-testid="general-config-separate-tenant-badge">Separate Tenant</Badge>
          </div>
          <div className={styles.configGrid} data-testid="general-config-config-grid-2-div">
            <Text className={styles.configLabel} data-testid="general-config-teams-tenant-id-text">Tenant ID:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-4-div">
              <span data-testid="general-config-teams-tenant-id-value-span">{config.tenants.teams.id}</span>
              <Button
              appearance="subtle"
              icon={<Copy24Regular data-testid="general-config-copy-teams-tenant-id-copy24-regular" />}
              size="small"
              onClick={() => handleCopyToClipboard(config.tenants.teams.id)} data-testid="general-config-copy-teams-tenant-id-button" />

            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-teams-tenant-name-text">Tenant Name:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-5-div">
              <span data-testid="general-config-teams-tenant-name-value-span">{config.tenants.teams.name}</span>
            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-teams-domain-text">Domain:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-6-div">
              <span data-testid="general-config-teams-tenant-domain-value-span">{config.tenants.teams.domain}</span>
            </div>
          </div>
        </Card>
      }

      {/* Frontend Application - Only show if configured */}
      {config.applications.frontend &&
      <Card className={styles.card} data-testid="general-config-frontend-app-card">
          <div className={styles.sectionTitle} data-testid="general-config-section-title-3-div">
            <Shield24Regular style={{ color: '#107c10' }} data-testid="general-config-shield24-regular" />
            Frontend Entra App Registration
          </div>
          <Text style={{ fontSize: '13px', color: '#605e5c', marginBottom: '16px' }} data-testid="general-config-this-app-registration-allows-users-to-text">
            This app registration allows users to sign in to the ADM web portal.
          </Text>
          <div className={styles.configGrid} data-testid="general-config-config-grid-3-div">
            <Text className={styles.configLabel} data-testid="general-config-frontend-application-name-text">Application Name:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-7-div">
              <span data-testid="general-config-frontend-app-name-value-span">{config.applications.frontend.displayName}</span>
            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-frontend-client-id-text">Client ID:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-8-div">
              <span data-testid="general-config-frontend-app-client-id-value-span">{config.applications.frontend.clientId}</span>
              <Button
              appearance="subtle"
              icon={<Copy24Regular data-testid="general-config-copy-frontend-client-id-copy24-regular" />}
              size="small"
              onClick={() => config.applications.frontend && handleCopyToClipboard(config.applications.frontend.clientId)} data-testid="general-config-copy-frontend-client-id-button" />

            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-frontend-graph-api-permissions-text">Graph API Permissions:</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="general-config-frontend-permissions-list-div">
              {config.applications.frontend.apiPermissions.map((permission, index) =>
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-frontend-permission-item-div">
                  <div className={styles.permissionItem} data-testid="general-config-permission-item-div">
                    <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} data-testid="general-config-frontend-permission-checkmark-circle24-regular" />
                    <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-frontend-permission-name-text">{permission}</Text>
                  </div>
                  <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-frontend-permission-description-text">
                    {getPermissionDescription(permission)}
                  </Text>
                </div>
            )}
              
              {missingPermissions(config.applications.frontend.apiPermissions, 'frontend').map((permission, index) =>
            <div key={`missing-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-frontend-missing-permission-item-div">
                  <div className={styles.permissionItem} style={{ backgroundColor: '#fff4ce', borderLeft: '3px solid #d83b01' }} data-testid="general-config-permission-item-2-div">
                    <ErrorCircle24Regular style={{ color: '#d83b01', fontSize: '16px' }} data-testid="general-config-frontend-missing-permission-error-circle24-regular" />
                    <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-frontend-missing-permission-name-text">{permission}</Text>
                    <Button
                  appearance="primary"
                  size="small"
                  icon={<ShieldKeyhole24Regular data-testid="general-config-frontend-grant-consent-shield-keyhole24-regular" />}
                  style={{ marginLeft: 'auto' }}
                  title={`Grant ${permission} permission for this app`}
                  onClick={() => handleAdminConsent(
                    config.applications.frontend!.clientId,
                    config.applications.frontend!.tenantId,
                    config.applications.frontend!.displayName,
                    permission
                  )} data-testid="general-config-frontend-grant-consent-button">

                      Grant Consent
                    </Button>
                  </div>
                  <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-frontend-missing-permission-description-text">
                    {getPermissionDescription(permission)}
                  </Text>
                </div>
            )}
              
              {missingPermissions(config.applications.frontend.apiPermissions, 'frontend').length > 1 &&
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }} data-testid="general-config-frontend-missing-permissions-actions-div">
                  <Button
                appearance="primary"
                size="medium"
                icon={<ShieldKeyhole24Regular data-testid="general-config-frontend-grant-all-shield-keyhole24-regular" />}
                onClick={() => handleAdminConsent(
                  config.applications.frontend!.clientId,
                  config.applications.frontend!.tenantId,
                  config.applications.frontend!.displayName
                )} data-testid="general-config-grant-all-missing-permissions-button">

                    Grant All Missing Permissions ({missingPermissions(config.applications.frontend.apiPermissions, 'frontend').length})
                  </Button>
                </div>
            }
            </div>
          </div>
        </Card>
      }

      {/* Backend Application - Azure/Entra */}
      <Card className={styles.card} id="backend-app-registration" data-testid="general-config-azure-backend-app-card">
        <div className={styles.sectionTitle} data-testid="general-config-section-title-4-div">
          <Key24Regular style={{ color: '#d13438' }} data-testid="general-config-azure-backend-key24-regular" />
          Backend Entra App Registration
        </div>
        <Text style={{ fontSize: '13px', color: '#605e5c', marginBottom: '16px' }} data-testid="general-config-this-app-registration-allows-the-adm-text">
          This app registration allows the ADM backend to manage Teams policy configuration and assignments on your behalf.
        </Text>
        <div className={styles.configGrid} data-testid="general-config-config-grid-4-div">
          <Text className={styles.configLabel} data-testid="general-config-azure-backend-application-name-text">Application Name:</Text>
          <div className={styles.configValue} data-testid="general-config-config-value-9-div">
            <span data-testid="general-config-azure-backend-app-name-value-span">{config.applications.backend.azure.displayName}</span>
          </div>
          
          <Text className={styles.configLabel} data-testid="general-config-azure-backend-client-id-text">Client ID:</Text>
          <div className={styles.configValue} data-testid="general-config-config-value-10-div">
            <span data-testid="general-config-azure-backend-app-client-id-value-span">{config.applications.backend.azure.clientId}</span>
            <Button
              appearance="subtle"
              icon={<Copy24Regular data-testid="general-config-copy-azure-backend-client-id-copy24-regular" />}
              size="small"
              onClick={() => handleCopyToClipboard(config.applications.backend.azure.clientId)} data-testid="general-config-copy-azure-backend-client-id-button" />

          </div>
          
          <Text className={styles.configLabel} data-testid="general-config-azure-backend-graph-api-permissions-text">Graph API Permissions:</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="general-config-azure-backend-permissions-list-div">
            {config.applications.backend.azure.apiPermissions.map((permission, index) =>
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-azure-backend-permission-item-div">
                <div className={styles.permissionItem} data-testid="general-config-permission-item-3-div">
                  <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} data-testid="general-config-azure-backend-permission-checkmark-circle24-regular" />
                  <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-azure-backend-permission-name-text">{permission}</Text>
                </div>
                <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-azure-backend-permission-description-text">
                  {getPermissionDescription(permission)}
                </Text>
              </div>
            )}
            
              {missingPermissions(config.applications.backend.azure.apiPermissions, 'azure-backend').map((permission, index) =>
            <div key={`missing-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-azure-backend-missing-permission-item-div">
                <div className={styles.permissionItem} style={{ backgroundColor: '#fff4ce', borderLeft: '3px solid #d83b01' }} data-testid="general-config-permission-item-4-div">
                  <ErrorCircle24Regular style={{ color: '#d83b01', fontSize: '16px' }} data-testid="general-config-azure-backend-missing-permission-error-circle24-regular" />
                  <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-azure-backend-missing-permission-name-text">{permission}</Text>
                  <Button
                  appearance="primary"
                  size="small"
                  icon={<ShieldKeyhole24Regular data-testid="general-config-azure-backend-grant-consent-shield-keyhole24-regular" />}
                  style={{ marginLeft: 'auto' }}
                  title={`Grant ${permission} permission for this app`}
                  onClick={() => handleAdminConsent(
                    config.applications.backend.azure.clientId,
                    config.applications.backend.azure.tenantId,
                    config.applications.backend.azure.displayName,
                    permission
                  )} data-testid="general-config-azure-backend-grant-consent-button">

                    Grant Consent
                  </Button>
                </div>
                <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-azure-backend-missing-permission-description-text">
                  {getPermissionDescription(permission)}
                </Text>
              </div>
            )}            
            {missingPermissions(config.applications.backend.azure.apiPermissions, 'azure-backend').length > 0 &&
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }} data-testid="general-config-azure-backend-missing-permissions-actions-div">
                <Button
                appearance="primary"
                size="medium"
                icon={<ShieldKeyhole24Regular data-testid="general-config-azure-backend-regrant-all-shield-keyhole24-regular" />}
                onClick={() => handleAdminConsent(
                  config.applications.backend.azure.clientId,
                  config.applications.backend.azure.tenantId,
                  config.applications.backend.azure.displayName
                )} data-testid="general-config-azure-backend-regrant-all-graph-api-permissions-button">

                  Re-grant all Graph API permissions ({missingPermissions(config.applications.backend.azure.apiPermissions, 'azure-backend').length})
                </Button>
              </div>
            }
          </div>
          
          {config.applications.backend.azure.directoryRoles && config.applications.backend.azure.directoryRoles.length > 0 &&
          <>
              <Text className={styles.configLabel} data-testid="general-config-azure-backend-directory-roles-text">Directory Roles:</Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="general-config-azure-backend-roles-list-div">
                {config.applications.backend.azure.directoryRoles.map((role, index) =>
              <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-azure-backend-role-item-div">
                    <div className={styles.permissionItem} data-testid="general-config-permission-item-5-div">
                      <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} data-testid="general-config-azure-backend-role-checkmark-circle24-regular" />
                      <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-azure-backend-role-name-text">{role}</Text>
                    </div>
                    <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-azure-backend-role-description-text">
                      {getDirectoryRoleDescription(role)}
                    </Text>
                  </div>
              )}
              </div>
            </>
          }

          {(!config.applications.backend.azure.directoryRoles || config.applications.backend.azure.directoryRoles.length === 0) &&
          <>
              <Text className={styles.configLabel} data-testid="general-config-azure-backend-missing-directory-roles-text">Directory Roles:</Text>
              <MessageBar intent="warning" data-testid="general-config-azure-backend-roles-warning-message-bar">
                <MessageBarBody data-testid="general-config-azure-backend-roles-warning-message-bar-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="general-config-azure-backend-roles-warning-content-div">
                    <Text style={{ fontWeight: '600' }} data-testid="general-config-azure-backend-teams-administrator-role-not-assigned-text">Teams Administrator role not assigned</Text>
                    <Text style={{ fontSize: '12px' }} data-testid="general-config-azure-backend-service-principal-needs-role-text">
                      The backend service principal needs the Teams Administrator role to manage Teams policies and assignments.
                      Please assign this role in Azure Portal: Azure AD → Roles and administrators → Teams Administrator → Add assignments
                    </Text>
                  </div>
                </MessageBarBody>
              </MessageBar>
            </>
          }
        </div>
      </Card>

      {/* Backend Application - Teams (if different) */}
      {!config.applications.backend.teams.isSameAsAzure &&
      <Card className={styles.card} data-testid="general-config-teams-backend-app-card">
          <div className={styles.sectionTitle} data-testid="general-config-section-title-5-div">
        <Key24Regular style={{ color: '#6264A7' }} data-testid="general-config-teams-backend-key24-regular" />
            Backend Entra App Registration
            <Badge appearance="outline" color="informative" data-testid="general-config-separate-app-badge">Separate App</Badge>
          </div>
          <div className={styles.configGrid} data-testid="general-config-config-grid-5-div">
            <Text className={styles.configLabel} data-testid="general-config-teams-backend-application-name-text">Application Name:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-11-div">
              <span data-testid="general-config-teams-backend-app-name-value-span">{config.applications.backend.teams.displayName}</span>
            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-teams-backend-client-id-text">Client ID:</Text>
            <div className={styles.configValue} data-testid="general-config-config-value-12-div">
              <span data-testid="general-config-teams-backend-app-client-id-value-span">{config.applications.backend.teams.clientId}</span>
              <Button
              appearance="subtle"
              icon={<Copy24Regular data-testid="general-config-copy-teams-backend-client-id-copy24-regular" />}
              size="small"
              onClick={() => handleCopyToClipboard(config.applications.backend.teams.clientId)} data-testid="general-config-copy-teams-backend-client-id-button" />

            </div>
            
            <Text className={styles.configLabel} data-testid="general-config-teams-backend-graph-api-permissions-text">Graph API Permissions:</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="general-config-teams-backend-permissions-list-div">
              {config.applications.backend.teams.apiPermissions.map((permission, index) =>
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-teams-backend-permission-item-div">
                  <div className={styles.permissionItem} data-testid="general-config-permission-item-6-div">
                    <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} data-testid="general-config-teams-backend-permission-checkmark-circle24-regular" />
                    <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-teams-backend-permission-name-text">{permission}</Text>
                  </div>
                  <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-teams-backend-permission-description-text">
                    {getPermissionDescription(permission)}
                  </Text>
                </div>
            )}
              
              {missingPermissions(config.applications.backend.teams.apiPermissions, 'teams-backend').map((permission, index) =>
            <div key={`missing-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-teams-backend-missing-permission-item-div">
                  <div className={styles.permissionItem} style={{ backgroundColor: '#fff4ce', borderLeft: '3px solid #d83b01' }} data-testid="general-config-permission-item-7-div">
                    <ErrorCircle24Regular style={{ color: '#d83b01', fontSize: '16px' }} data-testid="general-config-teams-backend-missing-permission-error-circle24-regular" />
                    <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-teams-backend-missing-permission-name-text">{permission}</Text>
                    <Button
                  appearance="primary"
                  size="small"
                  icon={<ShieldKeyhole24Regular data-testid="general-config-teams-backend-grant-consent-shield-keyhole24-regular" />}
                  style={{ marginLeft: 'auto' }}
                  title={`Grant ${permission} permission for this app`}
                  onClick={() => handleAdminConsent(
                    config.applications.backend.teams.clientId,
                    config.applications.backend.teams.tenantId,
                    config.applications.backend.teams.displayName,
                    permission
                  )} data-testid="general-config-teams-backend-grant-consent-button">

                      Grant Consent
                    </Button>
                  </div>
                  <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-teams-backend-missing-permission-description-text">
                    {getPermissionDescription(permission)}
                  </Text>
                </div>
            )}
              
              {missingPermissions(config.applications.backend.teams.apiPermissions, 'teams-backend').length > 0 &&
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }} data-testid="general-config-teams-backend-missing-permissions-actions-div">
                  <Button
                appearance="primary"
                size="medium"
                icon={<ShieldKeyhole24Regular data-testid="general-config-teams-backend-regrant-all-shield-keyhole24-regular" />}
                onClick={() => handleAdminConsent(
                  config.applications.backend.teams.clientId,
                  config.applications.backend.teams.tenantId,
                  config.applications.backend.teams.displayName
                )} data-testid="general-config-teams-backend-regrant-all-graph-api-permissions-button">

                    Re-grant all Graph API permissions ({missingPermissions(config.applications.backend.teams.apiPermissions, 'teams-backend').length})
                  </Button>
                </div>
            }
            </div>
            
            {config.applications.backend.teams.directoryRoles && config.applications.backend.teams.directoryRoles.length > 0 &&
          <>
                <Text className={styles.configLabel} data-testid="general-config-teams-backend-directory-roles-text">Directory Roles:</Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="general-config-teams-backend-roles-list-div">
                  {config.applications.backend.teams.directoryRoles.map((role, index) =>
              <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} data-testid="general-config-teams-backend-role-item-div">
                      <div className={styles.permissionItem} data-testid="general-config-permission-item-8-div">
                        <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} data-testid="general-config-teams-backend-role-checkmark-circle24-regular" />
                        <Text style={{ fontSize: '13px', fontWeight: '600' }} data-testid="general-config-teams-backend-role-name-text">{role}</Text>
                      </div>
                      <Text style={{ fontSize: '12px', color: '#605e5c', marginLeft: '28px' }} data-testid="general-config-teams-backend-role-description-text">
                        {getDirectoryRoleDescription(role)}
                      </Text>
                    </div>
              )}
                </div>
              </>
          }

            {(!config.applications.backend.teams.directoryRoles || config.applications.backend.teams.directoryRoles.length === 0) &&
          <>
                <Text className={styles.configLabel} data-testid="general-config-teams-backend-missing-directory-roles-text">Directory Roles:</Text>
                <MessageBar intent="warning" data-testid="general-config-teams-backend-roles-warning-message-bar">
                  <MessageBarBody data-testid="general-config-teams-backend-roles-warning-message-bar-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="general-config-teams-backend-roles-warning-content-div">
                      <Text style={{ fontWeight: '600' }} data-testid="general-config-teams-backend-teams-administrator-role-not-assigned-text">Teams Administrator role not assigned</Text>
                      <Text style={{ fontSize: '12px' }} data-testid="general-config-teams-backend-service-principal-needs-role-text">
                        The backend service principal needs the Teams Administrator role to manage Teams policies and assignments.
                        Please assign this role in Azure Portal: Azure AD → Roles and administrators → Teams Administrator → Add assignments
                      </Text>
                    </div>
                  </MessageBarBody>
                </MessageBar>
              </>
          }
          </div>
        </Card>
      }

      {/* Default Security Group */}
      <Card className={styles.card} style={{ display: 'none' }} data-testid="general-config-default-security-group-card">
        <div className={styles.sectionTitle} data-testid="general-config-section-title-6-div">
          <People24Regular style={{ color: '#0078d4' }} data-testid="general-config-people24-regular" />
          Default Security Group
        </div>
        <div className={styles.configGrid} data-testid="general-config-config-grid-6-div">
          <Text className={styles.configLabel} data-testid="general-config-default-group-for-new-policies-text">Default Group for New Policies:</Text>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} data-testid="general-config-default-group-input-row-div">
            <div style={{ flex: 1 }} data-testid="general-config-default-group-autocomplete-div">
              <GroupAutocomplete
                value={defaultGroupName}
                onChange={handleGroupChange}
                placeholder="Search for a security group..." data-testid="general-config-search-for-a-security-group-group-autocomplete" />

            </div>
            <Button
              appearance="primary"
              onClick={saveDefaultGroupSettings}
              disabled={!defaultGroup || savingDefaultGroup} data-testid="general-config-save-default-group-button">

              {savingDefaultGroup ? 'Saving...' : 'Save'}
            </Button>
          </div>
          
          {defaultGroup &&
          <>
              <Text className={styles.configLabel} data-testid="general-config-group-id-text">Group ID:</Text>
              <div className={styles.configValue} data-testid="general-config-config-value-13-div">
                <span data-testid="general-config-default-group-id-value-span">{defaultGroup.id}</span>
                <Button
                appearance="subtle"
                icon={<Copy24Regular data-testid="general-config-copy-default-group-id-copy24-regular" />}
                size="small"
                onClick={() => handleCopyToClipboard(defaultGroup.id)} data-testid="general-config-copy-default-group-id-button" />

              </div>
              
              {defaultGroup.description &&
            <>
                  <Text className={styles.configLabel} data-testid="general-config-description-text">Description:</Text>
                  <div className={styles.configValue} data-testid="general-config-config-value-14-div">
                    <span data-testid="general-config-default-group-description-value-span">{defaultGroup.description}</span>
                  </div>
                </>
            }
            </>
          }
          
          <Text className={styles.configLabel} data-testid="general-config-purpose-text">Purpose:</Text>
          <div className={styles.permissionsList} data-testid="general-config-permissions-list-div">
            <div className={styles.permissionItem} data-testid="general-config-permission-item-9-div">
              <Info24Regular style={{ color: '#0078d4', fontSize: '16px' }} data-testid="general-config-info24-regular" />
              <Text style={{ fontSize: '13px' }} data-testid="general-config-all-new-teams-policies-will-automatically-text">
                All new Teams policies will automatically be assigned to this security group
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </div>);

};